import { inject, Injectable } from '@angular/core';
import { DataService } from './data.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from '../component/dialog/confirmation-dialog/confirmation-dialog.component';
import { ControlMessageService } from './control-message.service';
import { LayoutService } from './layout.service';
import { firstValueFrom } from 'rxjs';
import { MkgOS } from '../class/mkg-os';
import { ChoosePhoneToMessageComponent } from '../component/dialog/choose-phone-to-message/choose-phone-to-message.component';
import { WhatsappMessageUtilities } from '../class/whatsapp-message-utilities';
import { ClientService } from './client.service';
import { WhatsappSendResult, WhatsappSkipReason } from '../interface/whatsapp-send-result';
import {
  WhatsappManualMessage,
  WhatsappPhoneCandidates,
  WhatsappPhoneResolution,
  WhatsappPlaceholders
} from '../interface/whatsapp-message';

const LOADER_KEY = 'WhatsappService.openDialog()';
const CS_PHONE = '554599920045';
const WA_ME_URL = 'https://wa.me';
const LINK_PLACEHOLDER_PATTERN = /{{\s*link\s*}}/i;
const DEFAULT_MESSAGES: Readonly<Record<string, string>> = {
  'ABRIR_ORÇAMENTO': 'TRACK_RO',
  'SOLICITAR_AUTORIZACAO': 'RO_READY',
  'ABRIR_OS': 'RO_OPENED',
  'FECHAR_OS': 'WHATS_FECHAR_OS',
  'RO_REJECTED': 'RO_REJECTED',
  'ACOMPANHAMENTO_OS': 'TRACK_RO',
  'undefined': 'TRACK_RO',
  'RO_UPDATED': 'RO_UPDATED',
};

@Injectable({
  providedIn: 'root'
})
export class WhatsappService {
  private readonly _dataService = inject(DataService);
  private readonly _dialog = inject(MatDialog);
  private readonly _translateService = inject(TranslateService);
  private readonly _controlMessageService = inject(ControlMessageService);
  private readonly _layout = inject(LayoutService);
  private readonly _clientService = inject(ClientService);

  /**
   * @param singlePhone number to use when the client has a single phone filled in (`phone2` only, for
   * instance); without it the service falls back to `phone1`, as the automatic flows always did.
   */
  async openDialog(label: string, ro: MkgOS, singlePhone?: string): Promise<WhatsappSendResult> {
    const link = await this._buildLink(label, ro, singlePhone);
    if (typeof link !== 'string') {
      return link;
    }

    const confirmed: boolean | undefined = await firstValueFrom(this._dialog
      .open(ConfirmationDialogComponent, {
        data: {
          text: 'SEND_LINK',
          link
        }
      })
      .afterClosed());

    return confirmed ? { status: 'sent', channel: 'manual' } : this._skip('user_cancelled');
  }

  async openWhatsCs(message: string): Promise<boolean> {
    const link = `${WA_ME_URL}/${CS_PHONE}?text=${encodeURIComponent(message)}`;
    window.open(link, '_blank');

    return true;
  }

  private async _buildLink(label: string, ro: MkgOS, singlePhone?: string): Promise<string | WhatsappSendResult> {
    this._layout.addLoaderTrigger(LOADER_KEY);

    try {
      const candidates = WhatsappMessageUtilities.resolvePhoneCandidates(ro);
      if ('skip' in candidates) {
        return this._skip(candidates.skip);
      }

      const company = this._dataService.company;
      if (!(company?.fancyName || company?.socialName) || !ro.vehicle) {
        return this._skip('missing_data');
      }

      const placeholders = WhatsappMessageUtilities.buildPlaceholders({ ro, company });
      const message = await this._buildMessage(label, placeholders);
      if (!message) {
        return this._skip('missing_template');
      }

      const phone = await this._resolvePhone(ro, candidates, singlePhone);
      if ('skip' in phone) {
        return this._skip(phone.skip);
      }

      const contactMessage = message.appendContactMessage ? ` ${this._translateService.instant('WHATSAPP.ADD_OUR_CONTACT')}` : '';
      const surveyLink = message.hasLinkPlaceholder ? '' : ` ${placeholders.link}`;
      const text = `${message.text}${contactMessage}${surveyLink}`;

      return `${WA_ME_URL}/${phone.phone}?text=${encodeURIComponent(text)}`;
    } finally {
      this._layout.removeLoaderTrigger(LOADER_KEY);
    }
  }

  private async _buildMessage(label: string, placeholders: WhatsappPlaceholders): Promise<WhatsappManualMessage | null> {
    const type = WhatsappMessageUtilities.labelToType(label);

    if (type !== null) {
      const messages = await this._controlMessageService.loadMessages();
      const template = messages.find(message => message.type === type)?.message;
      if (template) {
        return {
          text: WhatsappMessageUtilities.replacePlaceholders(template, placeholders),
          hasLinkPlaceholder: LINK_PLACEHOLDER_PATTERN.test(template),
          appendContactMessage: true
        };
      }
    }

    const defaultKey = DEFAULT_MESSAGES[label];
    if (defaultKey) {
      return {
        text: this._translateService.instant(`WHATSAPP.${defaultKey}`, { ...placeholders, code: placeholders.os }),
        hasLinkPlaceholder: false,
        appendContactMessage: true
      };
    }

    if (type !== null) {
      return null;
    }

    const hasLinkPlaceholder = LINK_PLACEHOLDER_PATTERN.test(label);
    return {
      text: WhatsappMessageUtilities.replacePlaceholders(label, placeholders),
      hasLinkPlaceholder,
      appendContactMessage: !hasLinkPlaceholder
    };
  }

  private async _resolvePhone(
    ro: MkgOS,
    candidates: Exclude<WhatsappPhoneCandidates, { skip: unknown }>,
    singlePhone?: string
  ): Promise<WhatsappPhoneResolution> {
    const raw = 'phone' in candidates ? candidates.phone || singlePhone : await this._choosePhone(ro, candidates.choose);
    if (!raw) {
      return { skip: 'user_cancelled' };
    }

    const phone = WhatsappMessageUtilities.normalizePhone(raw);
    return phone ? { phone } : { skip: 'invalid_phone' };
  }

  private async _choosePhone(ro: MkgOS, phones: [string, string]): Promise<string | undefined> {
    this._layout.removeLoaderTrigger(LOADER_KEY);
    const phone: string | undefined = await firstValueFrom(this._dialog.open(ChoosePhoneToMessageComponent, {
      data: phones,
      disableClose: true
    }).afterClosed());
    this._layout.addLoaderTrigger(LOADER_KEY);

    if (phone) {
      const clientFull = await this._clientService.get(ro.client.id);
      clientFull.whatsapp = phone;
      await this._clientService.update(clientFull);
    }

    return phone;
  }

  private _skip(reason: WhatsappSkipReason): WhatsappSendResult {
    return { status: 'skipped', reason };
  }
}
